import type { CSSProperties } from 'react'
import { useSceneClock } from '#app/utils/site/scene-clock'
import {
	BANK_COPY,
	BANK_FINAL_PHASE,
	BANK_KEYFRAMES,
	BANK_LOOP_MS,
	DECLARED_PERCENT,
	INCOME_BARS,
	RENT_MONTHS,
	STATEMENT_ROWS,
	bankSceneState,
	isSharedRow,
} from '#app/utils/site/scenes/bank-statement'
import { Wordmark } from '../../Wordmark'
import { MiniCard } from '../../primitives'
import type { SceneProps } from './types'

const FLINKS_LOGO = '/img/site/flinks.png'

/** The --i custom property the slot stylesheet reads for the staggers. */
function indexed(i: number, extra: Record<string, string> = {}): CSSProperties {
	return { '--i': i, ...extra } as CSSProperties
}

/**
 * "Who Else Has Your Bank Statements?": an emailed statement shows every
 * transaction, a scan leaves only income and rent, then the statement
 * dissolves into what the landlord sees through Flinks. The timeline and the
 * demo data live in app/utils/site/scenes/bank-statement.ts; the transitions
 * are in app/styles/site/scene-bank-statement.css, keyed off the data flags
 * set here. The tile's aria-label describes the scene, so the card itself is
 * decorative.
 */
export function BankStatementScene({ active }: SceneProps) {
	const { phase } = useSceneClock({
		active,
		keyframes: BANK_KEYFRAMES,
		loopMs: BANK_LOOP_MS,
		finalPhase: BANK_FINAL_PHASE,
	})
	const state = bankSceneState(phase)

	return (
		<MiniCard
			aria-hidden="true"
			data-scene="bank-statement"
			data-phase={phase}
			data-scanning={state.scanning || undefined}
			data-redacted={state.redacted || undefined}
			data-shared={state.shared || undefined}
			className="tt-scene-bank-statement @container relative isolate grid max-w-[450px] overflow-hidden leading-[1.25]"
		>
			<i className="tt-bank-scan" />
			<Statement />
			<LandlordView />
		</MiniCard>
	)
}

/** Phase A: the statement as the tenant emailed it. */
function Statement() {
	return (
		<div className="tt-bank-statement relative z-[1] [grid-area:1/1]">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<div className="text-[11px] font-medium uppercase tracking-[0.14em] text-tt-muted">
						{BANK_COPY.accountKind}
					</div>
					<div className="mt-1.5 text-[20px] font-semibold tracking-[-0.015em] @max-[340px]:text-[18px] @max-[300px]:text-[16px]">
						{BANK_COPY.title}
					</div>
				</div>
				<span className="tt-bank-mode relative inline-flex h-[30px] min-w-[118px] shrink-0 items-center justify-center rounded-full bg-tt-inset px-[13px] @max-[340px]:h-[26px] @max-[340px]:min-w-0 @max-[340px]:px-2.5">
					<span className="tt-bank-mode-a whitespace-nowrap text-[12px] font-semibold text-tt-body @max-[340px]:text-[11px]">
						{BANK_COPY.mode}
					</span>
					<span className="tt-bank-mode-b absolute inset-0 flex items-center justify-center">
						<Wordmark
							title={null}
							className="h-4 text-tt-ink @max-[340px]:h-3.5"
						/>
					</span>
				</span>
			</div>

			<div className="mt-3">
				{STATEMENT_ROWS.map((row, i) => (
					<div
						key={`${row.date}-${row.label}`}
						data-keep={isSharedRow(row) || undefined}
						style={indexed(i)}
						className="tt-bank-row relative grid grid-cols-[50px_1fr_auto] items-center gap-2.5 border-b border-tt-line-soft px-0.5 py-2.5 text-[13.5px] last:border-b-0"
					>
						<span className="tt-bank-date text-[12px] text-tt-muted">
							{row.date}
						</span>
						{/* Wraps instead of truncating, as the reference does on 360px
						    phones ("Streaming Subscription" does not fit one line). */}
						<span className="tt-bank-label min-w-0 break-words">
							{row.label}
						</span>
						<span className="tt-bank-amount tt-tabular font-semibold">
							{row.amount}
						</span>
					</div>
				))}
			</div>

			<div className="mt-3 flex items-center justify-between gap-2.5 text-[12px] text-tt-muted @max-[340px]:text-[11px]">
				<span className="tt-bank-acct">{BANK_COPY.footer}</span>
				<img
					className="tt-bank-flinks h-3.5 w-auto shrink-0"
					src={FLINKS_LOGO}
					alt=""
					width={409}
					height={155}
					loading="lazy"
					decoding="async"
				/>
			</div>
		</div>
	)
}

/** Phase B: what the landlord sees through Flinks. */
function LandlordView() {
	return (
		<div className="tt-bank-report relative z-[3] flex flex-col gap-4 [grid-area:1/1]">
			<div className="flex items-center justify-between">
				<Wordmark title={null} className="h-[17px] text-tt-ink" />
				<img
					className="h-6 w-auto"
					src={FLINKS_LOGO}
					alt=""
					width={409}
					height={155}
					loading="lazy"
					decoding="async"
				/>
			</div>

			<div className="rounded-2xl border border-tt-line-soft px-3.5 pb-3 pt-3.5">
				<div className="mb-2.5 flex items-baseline justify-between gap-2">
					<b className="text-[15px] font-semibold">{BANK_COPY.income}</b>
					<span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[11.5px] text-tt-muted">
						<i className="w-4 border-t-[1.5px] border-dashed border-[#9a9a96]" />
						{BANK_COPY.declared}
					</span>
				</div>
				<div className="relative flex h-[104px] items-end gap-[5px] pb-4 @max-[340px]:gap-1">
					<div
						className="absolute inset-x-0 z-[1] border-t-[1.5px] border-dashed border-[#9a9a96]"
						style={{
							bottom: `calc(16px + (100% - 16px) * ${DECLARED_PERCENT / 100})`,
						}}
					/>
					{INCOME_BARS.map((bar, i) => (
						<span
							key={`${bar.month}-${i}`}
							style={indexed(i, { '--h': `${bar.percent}%` })}
							className="relative flex h-full flex-1 flex-col items-center justify-end"
						>
							<i className="tt-bank-bar block w-full rounded-[5px] bg-tt-green" />
							<em className="absolute -bottom-4 text-[9.5px] not-italic text-tt-faint">
								{bar.month.charAt(0)}
							</em>
						</span>
					))}
				</div>
			</div>

			<div className="rounded-2xl border border-tt-line-soft px-3.5 pb-3 pt-3.5">
				<div className="mb-2.5 flex items-baseline justify-between gap-2">
					<b className="text-[15px] font-semibold">{BANK_COPY.rent}</b>
					<span className="whitespace-nowrap text-[11.5px] text-tt-muted @max-[340px]:text-[11px]">
						{BANK_COPY.rentSummary}
					</span>
				</div>
				<div className="grid grid-cols-6 gap-1.5 @max-[340px]:gap-1">
					{RENT_MONTHS.map((month, i) => (
						<span
							key={month}
							style={indexed(i)}
							className="flex flex-col items-center gap-[5px] rounded-[10px] border border-[#bfe3cb] pb-2 pt-[7px]"
						>
							<b className="text-[10.5px] font-semibold text-tt-ok">{month}</b>
							<span className="tt-bank-check flex h-5 w-5 items-center justify-center rounded-full bg-tt-ok-bg text-tt-green">
								<svg
									className="h-[13px] w-[13px]"
									viewBox="0 0 24 24"
									aria-hidden="true"
								>
									<path
										d="M6 12.5l4 4 8-8.5"
										fill="none"
										stroke="currentColor"
										strokeWidth={2.4}
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</span>
						</span>
					))}
				</div>
			</div>
		</div>
	)
}
